import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { FileText, Download, Eye, X, Award, Shield, ClipboardCheck } from 'lucide-react';
import SEOHead, { seoConfig } from '../components/SEOHead';

const CertificatesPage = () => {
  const { language } = useLanguage();
  const [selectedCert, setSelectedCert] = useState(null);
  const seo = seoConfig.certificates[language] || seoConfig.certificates.en || seoConfig.certificates.ru;

  const content = {
    ru: {
      header: {
        title: 'Сертификаты',
        subtitle: 'Вся продукция ТОО "Гипсовинил" сертифицирована и соответствует стандартам качества'
      },
      categories: {
        fireTests: 'Декларация соответствия и протокол пожарных испытаний',
        sesTests: 'Экспертное заключение с протоколами испытаний от СЭС',
        certificates: 'Сертификаты СТ-KZ'
      },
      actions: {
        view: 'Просмотреть',
        download: 'Скачать',
        close: 'Закрыть'
      },
      quality: {
        title: 'Гарантия качества',
        items: [
          { icon: Award, title: 'Сертифицированное производство', desc: 'Все процессы соответствуют международным стандартам' },
          { icon: Shield, title: 'Проверенные материалы', desc: 'Регулярные лабораторные испытания' },
          { icon: ClipboardCheck, title: 'Контроль качества', desc: 'Многоуровневая система проверки' }
        ]
      }
    },
    kz: {
      header: {
        title: 'Сертификаттар',
        subtitle: '"Гипсовинил" ЖШС-ның барлық өнімдері сертификатталған және сапа стандарттарына сәйкес келеді'
      },
      categories: {
        fireTests: 'Сәйкестік декларациясы және өрт сынау хаттамасы',
        sesTests: 'СЭС сараптама қорытындысы мен сынау хаттамалары',
        certificates: 'СТ-KZ сертификаттары'
      },
      actions: {
        view: 'Көру',
        download: 'Жүктеу',
        close: 'Жабу'
      },
      quality: {
        title: 'Сапа кепілдігі',
        items: [
          { icon: Award, title: 'Сертификатталған өндіріс', desc: 'Барлық процестер халықаралық стандарттарға сәйкес келеді' },
          { icon: Shield, title: 'Тексерілген материалдар', desc: 'Тұрақты зертханалық сынақтар' },
          { icon: ClipboardCheck, title: 'Сапаны бақылау', desc: 'Көп деңгейлі тексеру жүйесі' }
        ]
      }
    },
    en: {
      header: {
        title: 'Certificates',
        subtitle: 'All GIPSOVINIL.KZ products are certified and comply with quality standards'
      },
      categories: {
        fireTests: 'Declaration of Conformity and Fire Test Protocol',
        sesTests: 'Expert Opinion with Test Protocols from SES',
        certificates: 'ST-KZ Certificates'
      },
      actions: {
        view: 'View',
        download: 'Download',
        close: 'Close'
      },
      quality: {
        title: 'Quality Guarantee',
        items: [
          { icon: Award, title: 'Certified Production', desc: 'All processes comply with international standards' },
          { icon: Shield, title: 'Verified Materials', desc: 'Regular laboratory testing' },
          { icon: ClipboardCheck, title: 'Quality Control', desc: 'Multi-level inspection system' }
        ]
      }
    },
    ar: {
      header: {
        title: 'الشهادات',
        subtitle: 'جميع منتجات GIPSOVINIL.KZ معتمدة ومطابقة لمعايير الجودة'
      },
      categories: {
        fireTests: 'إعلان المطابقة وبروتوكول اختبار الحريق',
        sesTests: 'رأي الخبراء مع بروتوكولات الاختبار من SES',
        certificates: 'شهادات ST-KZ'
      },
      actions: {
        view: 'عرض',
        download: 'تنزيل',
        close: 'إغلاق'
      },
      quality: {
        title: 'ضمان الجودة',
        items: [
          { icon: Award, title: 'إنتاج معتمد', desc: 'جميع العمليات مطابقة للمعايير الدولية' },
          { icon: Shield, title: 'مواد موثّقة', desc: 'اختبارات مخبرية دورية منتظمة' },
          { icon: ClipboardCheck, title: 'مراقبة الجودة', desc: 'نظام فحص متعدد المستويات' }
        ]
      }
    }
  };

  // Certificate data with Google Drive links
  const certificates = [
    {
      id: 1,
      category: 'fireTests',
      name: {
        ru: 'Декларация соответствия',
        kz: 'Сәйкестік декларациясы',
        en: 'Declaration of Conformity', ar: 'إعلان المطابقة'
      },
      description: {
        ru: 'Официальная декларация о соответствии продукции требованиям технических регламентов',
        kz: 'Өнімнің техникалық регламенттер талаптарына сәйкестігі туралы ресми декларация',
        en: 'Official declaration of product conformity to technical regulations', ar: 'إعلان رسمي بمطابقة المنتج للوائح التقنية'
      },
      fileSize: '192 KB',
      // Use PDF URL for preview via Google Docs Viewer
      pdfUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/47hlyyyi_%D0%94%D0%B5%D0%BA%D0%BB%D0%B0%D1%80%D0%B0%D1%86%D0%B8%D1%8F%20%D1%81%D0%BE%D0%BE%D1%82%D0%B2%D0%B5%D1%82%D1%81%D1%82%D0%B2%D0%B8%D1%8F.pdf',
      viewUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/47hlyyyi_%D0%94%D0%B5%D0%BA%D0%BB%D0%B0%D1%80%D0%B0%D1%86%D0%B8%D1%8F%20%D1%81%D0%BE%D0%BE%D1%82%D0%B2%D0%B5%D1%82%D1%81%D1%82%D0%B2%D0%B8%D1%8F.pdf',
      downloadUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/47hlyyyi_%D0%94%D0%B5%D0%BA%D0%BB%D0%B0%D1%80%D0%B0%D1%86%D0%B8%D1%8F%20%D1%81%D0%BE%D0%BE%D1%82%D0%B2%D0%B5%D1%82%D1%81%D1%82%D0%B2%D0%B8%D1%8F.pdf',
      driveUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/47hlyyyi_%D0%94%D0%B5%D0%BA%D0%BB%D0%B0%D1%80%D0%B0%D1%86%D0%B8%D1%8F%20%D1%81%D0%BE%D0%BE%D1%82%D0%B2%D0%B5%D1%82%D1%81%D1%82%D0%B2%D0%B8%D1%8F.pdf'
    },
    {
      id: 2,
      category: 'fireTests',
      name: {
        ru: 'Протокол пожарных испытаний',
        kz: 'Өрт сынау хаттамасы',
        en: 'Fire Test Protocol', ar: 'بروتوكول اختبار الحريق'
      },
      description: {
        ru: 'Результаты лабораторных испытаний продукции на соответствие стандартам пожарной безопасности',
        kz: 'Өнімнің өрт қауіпсіздігі стандарттарына сәйкестігін зертханалық сынау нәтижелері',
        en: 'Laboratory test results for product compliance with fire safety standards', ar: 'نتائج الاختبارات المخبرية لمطابقة المنتج لمعايير السلامة من الحريق'
      },
      fileSize: '2 MB',
      pdfUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/mo8l5a29_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86.pdf',
      viewUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/mo8l5a29_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86.pdf',
      downloadUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/mo8l5a29_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86.pdf',
      driveUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/mo8l5a29_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86.pdf'
    },
    {
      id: 7,
      category: 'sesTests',
      name: {
        ru: 'Экспертное заключение ТМ Гипсовинил',
        kz: 'Гипсовинил ТМ сараптама қорытындысы',
        en: 'Expert Opinion TM GIPSOVINIL.KZ', ar: 'رأي الخبراء للعلامة التجارية GIPSOVINIL.KZ'
      },
      description: {
        ru: 'Официальное экспертное заключение о качестве и безопасности продукции',
        kz: 'Өнімнің сапасы мен қауіпсіздігі туралы ресми сараптама қорытындысы',
        en: 'Official expert opinion on product quality and safety', ar: 'رأي الخبراء الرسمي حول جودة المنتج وسلامته'
      },
      fileSize: '13.9 MB',
      pdfUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/n7b9gomo_%D0%AD%D0%BA%D1%81%D0%BF%D0%B5%D1%80%D1%82%D0%BD%D0%BE%D0%B5%20%D0%B7%D0%B0%D0%BA%D0%BB%D1%8E%D1%87%D0%B5%D0%BD%D0%B8%D0%B5%20%D0%A2%D0%9C%20%D0%93%D0%B8%D0%BF%D1%81%D0%BE%D0%B2%D0%B8%D0%BD%D0%B8%D0%BB.pdf',
      viewUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/n7b9gomo_%D0%AD%D0%BA%D1%81%D0%BF%D0%B5%D1%80%D1%82%D0%BD%D0%BE%D0%B5%20%D0%B7%D0%B0%D0%BA%D0%BB%D1%8E%D1%87%D0%B5%D0%BD%D0%B8%D0%B5%20%D0%A2%D0%9C%20%D0%93%D0%B8%D0%BF%D1%81%D0%BE%D0%B2%D0%B8%D0%BD%D0%B8%D0%BB.pdf',
      downloadUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/n7b9gomo_%D0%AD%D0%BA%D1%81%D0%BF%D0%B5%D1%80%D1%82%D0%BD%D0%BE%D0%B5%20%D0%B7%D0%B0%D0%BA%D0%BB%D1%8E%D1%87%D0%B5%D0%BD%D0%B8%D0%B5%20%D0%A2%D0%9C%20%D0%93%D0%B8%D0%BF%D1%81%D0%BE%D0%B2%D0%B8%D0%BD%D0%B8%D0%BB.pdf',
      driveUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/n7b9gomo_%D0%AD%D0%BA%D1%81%D0%BF%D0%B5%D1%80%D1%82%D0%BD%D0%BE%D0%B5%20%D0%B7%D0%B0%D0%BA%D0%BB%D1%8E%D1%87%D0%B5%D0%BD%D0%B8%D0%B5%20%D0%A2%D0%9C%20%D0%93%D0%B8%D0%BF%D1%81%D0%BE%D0%B2%D0%B8%D0%BD%D0%B8%D0%BB.pdf'
    },
    {
      id: 3,
      category: 'sesTests',
      name: {
        ru: 'Протокол лабораторных испытаний №1',
        kz: 'Зертханалық сынау хаттамасы №1',
        en: 'Laboratory Test Protocol #1', ar: 'بروتوكول الاختبار المخبري رقم 1'
      },
      description: {
        ru: 'Детальные результаты лабораторных испытаний материалов',
        kz: 'Материалдарды зертханалық сынаудың егжей-тегжейлі нәтижелері',
        en: 'Detailed laboratory test results for materials', ar: 'نتائج الاختبارات المخبرية التفصيلية للمواد'
      },
      fileSize: '3.2 MB',
      pdfUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/2x2h2qnl_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%BB%D0%B0%D0%B1%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86%202.pdf',
      viewUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/2x2h2qnl_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%BB%D0%B0%D0%B1%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86%202.pdf',
      downloadUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/2x2h2qnl_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%BB%D0%B0%D0%B1%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86%202.pdf',
      driveUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/2x2h2qnl_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%BB%D0%B0%D0%B1%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86%202.pdf'
    },
    {
      id: 4,
      category: 'sesTests',
      name: {
        ru: 'Протокол лабораторных испытаний №2',
        kz: 'Зертханалық сынау хаттамасы №2',
        en: 'Laboratory Test Protocol #2', ar: 'بروتوكول الاختبار المخبري رقم 2'
      },
      description: {
        ru: 'Дополнительные результаты лабораторных испытаний',
        kz: 'Зертханалық сынаудың қосымша нәтижелері',
        en: 'Additional laboratory test results', ar: 'نتائج الاختبارات المخبرية الإضافية'
      },
      fileSize: '3.3 MB',
      pdfUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/utdy853s_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%BB%D0%B0%D0%B1%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86%202.pdf',
      viewUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/utdy853s_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%BB%D0%B0%D0%B1%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86%202.pdf',
      downloadUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/utdy853s_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%BB%D0%B0%D0%B1%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86%202.pdf',
      driveUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/utdy853s_%D0%9F%D1%80%D0%BE%D1%82%D0%BE%D0%BA%D0%BE%D0%BB%20%D0%BB%D0%B0%D0%B1%20%D0%B8%D1%81%D0%BF%D1%8B%D1%82%D0%B0%D0%BD%D0%B8%D0%B8%CC%86%202.pdf'
    },
    {
      id: 5,
      category: 'certificates',
      name: {
        ru: 'Сертификат СТ-KZ (казахский)',
        kz: 'СТ-KZ сертификаты (қазақша)',
        en: 'ST-KZ Certificate (Kazakh)', ar: 'شهادة ST-KZ (بالكازاخية)'
      },
      description: {
        ru: 'Сертификат о происхождении товара на казахском языке',
        kz: 'Тауардың шығу тегі туралы сертификат қазақ тілінде',
        en: 'Certificate of origin in Kazakh language', ar: 'شهادة المنشأ باللغة الكازاخية'
      },
      fileSize: '2.4 MB',
      pdfUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/rpm98vfi_%D0%A1%D0%B5%D1%80%D1%82%D0%B8%D1%84%D0%B8%D0%BA%D0%B0%D1%82%20%D0%A4%D0%9E%D0%A0%D0%9C%D0%90%20%D0%A1%D0%A2-KZ%20%D0%BD%D0%B0%20%D0%BA%D0%B0%D0%B7%D0%B0%D1%85%D1%81%D0%BA%D0%BE%D0%BC.pdf',
      viewUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/rpm98vfi_%D0%A1%D0%B5%D1%80%D1%82%D0%B8%D1%84%D0%B8%D0%BA%D0%B0%D1%82%20%D0%A4%D0%9E%D0%A0%D0%9C%D0%90%20%D0%A1%D0%A2-KZ%20%D0%BD%D0%B0%20%D0%BA%D0%B0%D0%B7%D0%B0%D1%85%D1%81%D0%BA%D0%BE%D0%BC.pdf',
      downloadUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/rpm98vfi_%D0%A1%D0%B5%D1%80%D1%82%D0%B8%D1%84%D0%B8%D0%BA%D0%B0%D1%82%20%D0%A4%D0%9E%D0%A0%D0%9C%D0%90%20%D0%A1%D0%A2-KZ%20%D0%BD%D0%B0%20%D0%BA%D0%B0%D0%B7%D0%B0%D1%85%D1%81%D0%BA%D0%BE%D0%BC.pdf',
      driveUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/rpm98vfi_%D0%A1%D0%B5%D1%80%D1%82%D0%B8%D1%84%D0%B8%D0%BA%D0%B0%D1%82%20%D0%A4%D0%9E%D0%A0%D0%9C%D0%90%20%D0%A1%D0%A2-KZ%20%D0%BD%D0%B0%20%D0%BA%D0%B0%D0%B7%D0%B0%D1%85%D1%81%D0%BA%D0%BE%D0%BC.pdf'
    },
    {
      id: 6,
      category: 'certificates',
      name: {
        ru: 'Сертификат СТ-KZ (русский)',
        kz: 'СТ-KZ сертификаты (орысша)',
        en: 'ST-KZ Certificate (Russian)', ar: 'شهادة ST-KZ (بالروسية)'
      },
      description: {
        ru: 'Сертификат о происхождении товара на русском языке',
        kz: 'Тауардың шығу тегі туралы сертификат орыс тілінде',
        en: 'Certificate of origin in Russian language', ar: 'شهادة المنشأ باللغة الروسية'
      },
      fileSize: '70 KB',
      pdfUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/rp9fxpjt_%D0%A1%D0%B5%D1%80%D1%82%D0%B8%D1%84%D0%B8%D0%BA%D0%B0%D1%82%20%D0%A4%D0%9E%D0%A0%D0%9C%D0%90%20%D0%A1%D0%A2-KZ%20%D0%BD%D0%B0%20%D1%80%D1%83%D1%81.pdf',
      viewUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/rp9fxpjt_%D0%A1%D0%B5%D1%80%D1%82%D0%B8%D1%84%D0%B8%D0%BA%D0%B0%D1%82%20%D0%A4%D0%9E%D0%A0%D0%9C%D0%90%20%D0%A1%D0%A2-KZ%20%D0%BD%D0%B0%20%D1%80%D1%83%D1%81.pdf',
      downloadUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/rp9fxpjt_%D0%A1%D0%B5%D1%80%D1%82%D0%B8%D1%84%D0%B8%D0%BA%D0%B0%D1%82%20%D0%A4%D0%9E%D0%A0%D0%9C%D0%90%20%D0%A1%D0%A2-KZ%20%D0%BD%D0%B0%20%D1%80%D1%83%D1%81.pdf',
      driveUrl: 'https://customer-assets.emergentagent.com/job_93f455b0-e7c1-4582-a7b3-cdb5add67205/artifacts/rp9fxpjt_%D0%A1%D0%B5%D1%80%D1%82%D0%B8%D1%84%D0%B8%D0%BA%D0%B0%D1%82%20%D0%A4%D0%9E%D0%A0%D0%9C%D0%90%20%D0%A1%D0%A2-KZ%20%D0%BD%D0%B0%20%D1%80%D1%83%D1%81.pdf'
    }
  ];

  const t = content[language] || content.en || content.ru;

  const openCertificate = (cert) => {
    // Open PDF directly in new tab instead of modal
    window.open(cert.viewUrl || cert.pdfUrl || cert.driveUrl, '_blank');
  };

  const closeCertificate = () => {
    setSelectedCert(null);
    document.body.style.overflow = 'unset';
  };

  // Group certificates by category
  const groupedCerts = {
    fireTests: certificates.filter(c => c.category === 'fireTests'),
    sesTests: certificates.filter(c => c.category === 'sesTests'),
    certificates: certificates.filter(c => c.category === 'certificates')
  };

  // Google Drive folder for download all
  const driveFolderUrl = 'https://drive.google.com/drive/folders/19dR23jhiQIx4HHcB1Ysb9rtDLI6_sOqK';

  return (
    <div className="pt-20 bg-[#061d36]" data-testid="certificates-page">
      <SEOHead 
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
      />
      {/* Certificate Modal */}
      {selectedCert && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
          onClick={closeCertificate}
        >
          <div 
            className="relative w-full max-w-5xl h-[90vh] bg-white rounded-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-[#061d36] text-white">
              <div>
                <h3 className="text-lg font-bold">{selectedCert.name[language]}</h3>
                <p className="text-sm text-blue-200">{selectedCert.fileSize}</p>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={selectedCert.downloadUrl || selectedCert.driveUrl || driveFolderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-[#8B4444] hover:bg-[#7A3838] rounded-lg text-white font-medium transition-colors"
                  data-testid="modal-download-btn"
                >
                  <Download className="w-5 h-5" />
                  {t.actions.download}
                </a>
                <button
                  onClick={closeCertificate}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  data-testid="modal-close-btn"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* PDF Viewer */}
            <div className="flex-1 bg-gray-100">
              {selectedCert.viewUrl ? (
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedCert.viewUrl)}&embedded=true`}
                  className="w-full h-full"
                  title={selectedCert.name[language]}
                  frameBorder="0"
                />
              ) : (
                <iframe
                  src={`https://drive.google.com/embeddedfolderview?id=19dR23jhiQIx4HHcB1Ysb9rtDLI6_sOqK#list`}
                  className="w-full h-full"
                  title={selectedCert.name[language]}
                  frameBorder="0"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <section className="py-8 pb-16 bg-[#061d36] text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="certificates-title">{t.header.title}</h1>
          <p className="text-lg text-blue-200 max-w-3xl mx-auto">{t.header.subtitle}</p>
        </div>
      </section>

      {/* Quality Badges */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {t.quality.items.map((item, idx) => {
              const IconComponent = item.icon;
              return (
                <div key={idx} className="flex items-start gap-4 p-6 bg-[#F5F5F0] rounded-2xl">
                  <div className="w-12 h-12 bg-[#061d36] rounded-xl flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#061d36] mb-1">{item.title}</h3>
                    <p className="text-[#061d36]/70 text-sm">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Download All Button */}
      <section className="py-6 bg-[#F5F5F0]">
        <div className="container mx-auto px-4 text-center">
          <a
            href={driveFolderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-colors"
            data-testid="download-all-btn"
          >
            <Download className="w-5 h-5" />
            {language === 'ru' ? 'Скачать все сертификаты' : 
             language === 'kz' ? 'Барлық сертификаттарды жүктеу' : 
             'Download all certificates'}
          </a>
        </div>
      </section>

      {/* Certificates by Category */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          {/* Fire Tests - Declaration and Fire Protocol */}
          {groupedCerts.fireTests.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#061d36] mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                {t.categories.fireTests}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupedCerts.fireTests.map((cert) => (
                  <CertificateCard 
                    key={cert.id} 
                    cert={cert} 
                    language={language} 
                    t={t}
                    driveUrl={cert.downloadUrl || cert.driveUrl || driveFolderUrl}
                  />
                ))}
              </div>
            </div>
          )}

          {/* SES Expert Opinion with Test Protocols */}
          {groupedCerts.sesTests.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#061d36] mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <ClipboardCheck className="w-5 h-5 text-green-600" />
                </div>
                {t.categories.sesTests}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupedCerts.sesTests.map((cert) => (
                  <CertificateCard 
                    key={cert.id} 
                    cert={cert} 
                    language={language} 
                    t={t}
                    driveUrl={cert.downloadUrl || cert.driveUrl || driveFolderUrl}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Certificates */}
          {groupedCerts.certificates.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#061d36] mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Award className="w-5 h-5 text-amber-600" />
                </div>
                {t.categories.certificates}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupedCerts.certificates.map((cert) => (
                  <CertificateCard 
                    key={cert.id} 
                    cert={cert} 
                    language={language} 
                    t={t}
                    driveUrl={cert.downloadUrl || cert.driveUrl || driveFolderUrl}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="pt-16 py-8 pb-16 bg-[#061d36]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {language === 'ru' ? 'Нужна дополнительная документация?' : 
             language === 'kz' ? 'Қосымша құжаттама қажет пе?' : 
             'Need additional documentation?'}
          </h2>
          <p className="text-blue-200 mb-8 max-w-2xl mx-auto">
            {language === 'ru' ? 'Свяжитесь с нами для получения любых дополнительных документов и сертификатов' : 
             language === 'kz' ? 'Кез келген қосымша құжаттар мен сертификаттар алу үшін бізбен хабарласыңыз' : 
             'Contact us to obtain any additional documents and certificates'}
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-colors"
          >
            {language === 'ru' ? 'Связаться с нами' : 
             language === 'kz' ? 'Бізбен байланысу' : 
             'Contact us'}
          </a>
        </div>
      </section>
    </div>
  );
};

// Certificate Card Component
const CertificateCard = ({ cert, language, t, driveUrl }) => {
  const handleView = () => {
    window.open(cert.viewUrl || cert.pdfUrl || driveUrl, '_blank');
  };

  return (
    <div 
      className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all group"
      data-testid={`certificate-card-${cert.id}`}
    >
      {/* PDF Preview - Static Design for Fast Loading */}
      <div 
        className="h-48 bg-gradient-to-br from-[#0E2A47] to-[#1a4a6e] flex items-center justify-center cursor-pointer relative overflow-hidden"
        onClick={handleView}
      >
        {/* Decorative elements */}
        <div className="absolute top-4 left-4 w-20 h-20 bg-white/5 rounded-full"></div>
        <div className="absolute bottom-4 right-4 w-32 h-32 bg-white/5 rounded-full"></div>
        
        {/* PDF Icon */}
        <div className="text-center z-10">
          <div className="w-20 h-24 bg-white rounded-xl shadow-lg flex flex-col items-center justify-center mb-3 mx-auto transform group-hover:scale-105 transition-transform">
            <FileText className="w-10 h-10 text-[#8b4444] mb-1" />
            <span className="text-sm font-bold text-[#8b4444]">PDF</span>
          </div>
          <span className="text-white/90 text-sm font-medium">{cert.fileSize}</span>
        </div>
        
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-[#061d36]/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
            <Eye className="w-7 h-7 text-white" />
          </div>
        </div>
      </div>
      
      {/* Info */}
      <div className="p-5">
        <h3 className="font-bold text-[#061d36] mb-2 line-clamp-2">{cert.name[language]}</h3>
        <p className="text-[#061d36]/70 text-sm mb-4 line-clamp-2">{cert.description[language]}</p>
        
        {/* Actions */}
        <div className="flex gap-2">
          <a
            href={cert.viewUrl || cert.pdfUrl || driveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-[#061d36] text-white rounded-xl font-medium hover:bg-[#0d2a47] transition-colors text-sm"
          >
            <Eye className="w-4 h-4" />
            {t.actions.view}
          </a>
          <a
            href={driveUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-medium transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            {t.actions.download}
          </a>
        </div>
      </div>
    </div>
  );
};

export default CertificatesPage;
